export type LeagueBackupVariant = "full" | "registrations";

export interface LeagueBackupPreview {
  backup: Record<string, unknown>;
  fileName: string;
  sizeBytes: number;
  variant: LeagueBackupVariant;
  schemaVersion: 1 | 2 | 3 | 4;
  legacyNoPollSnapshot: boolean;
  legacyNoFinalizationSnapshot: boolean;
  legacyNoStandardDraftOrderSnapshot: boolean;
  warnings: string[];
  season: number;
  cycleId: string;
  exportedAt: string;
  exportedAtMs: number;
  summary: {
    userCount: number;
    membershipCount: number;
    teamCount: number;
    assignedTeamCount: number;
    pollBallotCount: number;
    pollFinalized: boolean;
  };
}

export const MAX_LEAGUE_BACKUP_BYTES = 8 * 1024 * 1024;
const SUPPORTED_D1_BACKUP_MIGRATION = 23;
const PREVIOUS_D1_BACKUP_MIGRATION = 22;
const OLDER_D1_BACKUP_MIGRATION = 14;
const LEGACY_D1_BACKUP_MIGRATION = 12;
const SUPPORTED_DRAFT_ROOM_BACKUP_SCHEMA = 1;
export const LEGACY_NO_POLL_BACKUP_WARNING = "This legacy backup predates the standard-draft poll. Restoring it will clear all current poll ballots.";
export const LEGACY_NO_POLL_FINALIZATION_WARNING = "This backup predates frozen poll results. Restoring it will clear any current finalized result and Standard Draft time so the restored ballots can be finalized safely.";
export const LEGACY_NO_STANDARD_DRAFT_ORDER_WARNING = "This full backup predates the persisted Standard Draft order. Restoring it will use the official 2026 order because the older file could not contain one.";

const RESTORE_PRE_WRITE_REJECTION_CODES = new Set([
  "backup_invalid",
  "backup_signature_invalid",
  "backup_confirmation_required",
  "password_confirmation_failed",
  "commissioner_required",
  "request_too_large",
]);

export function leagueRestoreFailureGuidance(code: string): string {
  return RESTORE_PRE_WRITE_REJECTION_CODES.has(code)
    ? "No league data was changed."
    : "The restore may still be in progress. Keep this file selected and retry; the same operation key prevents duplicate work.";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonNegativeInteger(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function safeFileName(name: string): string {
  return name.replace(/[\u0000-\u001f\u007f]/gu, "").slice(0, 200);
}

export function formatBackupFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function parseLeagueBackupDocument(
  document: unknown,
  file: { name: string; size: number; type?: string },
): LeagueBackupPreview {
  if (!file.name.toLocaleLowerCase("en-US").endsWith(".json")) throw new Error("Choose a .json League Hub backup file.");
  if (!Number.isSafeInteger(file.size) || file.size <= 0) throw new Error("The selected backup file is empty.");
  if (file.size > MAX_LEAGUE_BACKUP_BYTES) throw new Error("The selected backup file is too large.");
  const mimeType = file.type?.split(";", 1)[0]?.trim().toLocaleLowerCase("en-US") ?? "";
  if (mimeType && mimeType !== "application/json" && mimeType !== "text/json") throw new Error("Choose a JSON League Hub backup file.");
  if (!isRecord(document)
    || document.schema !== "team-beer.league-backup"
    || (document.schemaVersion !== 1 && document.schemaVersion !== 2 && document.schemaVersion !== 3 && document.schemaVersion !== 4)
    || (document.variant !== "full" && document.variant !== "registrations")
    || !isRecord(document.compatibility)
    || !isRecord(document.league)
    || !isRecord(document.summary)
    || !isRecord(document.payload)
    || !isRecord(document.integrity)) {
    throw new Error("This file is not a recognized League Hub backup.");
  }

  const legacyNoPollSnapshot = document.schemaVersion === 1;
  const legacyNoFinalizationSnapshot = document.schemaVersion < 3;
  const legacyNoStandardDraftOrderSnapshot = document.variant === "full" && document.schemaVersion < 4;
  const supportedMigration = document.schemaVersion === 1
    ? LEGACY_D1_BACKUP_MIGRATION
    : document.schemaVersion === 2
      ? OLDER_D1_BACKUP_MIGRATION
      : document.schemaVersion === 3
        ? PREVIOUS_D1_BACKUP_MIGRATION
        : SUPPORTED_D1_BACKUP_MIGRATION;
  if (document.compatibility.d1Migration !== supportedMigration
    || document.compatibility.draftRoomSchema !== SUPPORTED_DRAFT_ROOM_BACKUP_SCHEMA
    || document.league.leagueId !== "team-beer"
    || document.league.cycleId !== "team-beer-2026"
    || document.league.season !== 2026) {
    throw new Error("This backup is for an unsupported League Hub version, league, or season.");
  }

  const season = nonNegativeInteger(document.league.season);
  const cycleId = typeof document.league.cycleId === "string" ? document.league.cycleId.trim() : "";
  const exportedAt = typeof document.exportedAt === "string" ? document.exportedAt.trim() : "";
  const exportedAtMs = Date.parse(exportedAt);
  const userCount = nonNegativeInteger(document.summary.userCount);
  const membershipCount = nonNegativeInteger(document.summary.membershipCount);
  const teamCount = nonNegativeInteger(document.summary.teamCount);
  const assignedTeamCount = nonNegativeInteger(document.summary.assignedTeamCount);
  const pollBallotCount = legacyNoPollSnapshot
    ? 0
    : nonNegativeInteger(document.summary.pollBallotCount);
  const pollFinalized = legacyNoFinalizationSnapshot
    ? false
    : document.summary.pollFinalized;
  const algorithm = document.integrity.algorithm;
  const digest = document.integrity.digest;

  if (season === null || !cycleId || !exportedAt || !Number.isFinite(exportedAtMs)
    || userCount === null || membershipCount === null || teamCount === null || assignedTeamCount === null
    || pollBallotCount === null
    || typeof pollFinalized !== "boolean"
    || algorithm !== "HMAC-SHA-256" || typeof digest !== "string" || !/^[A-Za-z0-9_-]{43}$/u.test(digest)) {
    throw new Error("This League Hub backup has incomplete or invalid metadata.");
  }

  return {
    backup: document,
    fileName: safeFileName(file.name),
    sizeBytes: file.size,
    variant: document.variant,
    schemaVersion: document.schemaVersion,
    legacyNoPollSnapshot,
    legacyNoFinalizationSnapshot,
    legacyNoStandardDraftOrderSnapshot,
    warnings: [
      ...(legacyNoPollSnapshot ? [LEGACY_NO_POLL_BACKUP_WARNING] : []),
      ...(legacyNoFinalizationSnapshot ? [LEGACY_NO_POLL_FINALIZATION_WARNING] : []),
      ...(legacyNoStandardDraftOrderSnapshot ? [LEGACY_NO_STANDARD_DRAFT_ORDER_WARNING] : []),
    ],
    season,
    cycleId,
    exportedAt,
    exportedAtMs,
    summary: { userCount, membershipCount, teamCount, assignedTeamCount, pollBallotCount, pollFinalized },
  };
}

export async function inspectLeagueBackupFile(file: File): Promise<LeagueBackupPreview> {
  if (file.size <= 0) throw new Error("The selected backup file is empty.");
  if (file.size > MAX_LEAGUE_BACKUP_BYTES) throw new Error("The selected backup file is too large.");
  let document: unknown;
  try {
    document = JSON.parse(await file.text()) as unknown;
  } catch {
    throw new Error("The selected file does not contain valid JSON.");
  }
  return parseLeagueBackupDocument(document, file);
}
